import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Send, Loader2, CheckCircle2, XCircle, HourglassIcon, Pencil, X } from 'lucide-react';
import BranchShell from '../components/BranchShell';
import DeskButton from '../components/DeskButton';
import OvertimeSlaChip from '../components/OvertimeSlaChip';
import { API, useBranchAuth } from '../context/BranchAuthContext';

/**
 * Feb 2026 — BranchMyWork
 *
 * Branch mirror of `AdminMyWork`. Submits overtime claims + shows a
 * personal desk-activity log. Uses branch JWT + `/api/branch/…`
 * endpoints so the super-admin combined report picks these rows up
 * under `actor_type='branch'`.
 */

const mudDate = () => {
  const d = new Date();
  const shifted = new Date(d.getTime() + 4 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
};

const StatusChip = ({ status }) => {
  const map = {
    pending:  { bg: '#FEF3C7', ink: '#92400E', label: 'Pending',  Icon: HourglassIcon },
    approved: { bg: '#D1FAE5', ink: '#065F46', label: 'Approved', Icon: CheckCircle2 },
    rejected: { bg: '#FEE2E2', ink: '#991B1B', label: 'Rejected', Icon: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: s.bg, color: s.ink }}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const BranchMyWork = () => {
  const { authHeaders, branch } = useBranchAuth();
  const [otDate, setOtDate] = useState(mudDate());
  const [otHours, setOtHours] = useState(2);
  const [otReason, setOtReason] = useState('');
  // Feb 2026 — Overtime is fully auto-computed on the backend
  // (see AdminMyWork). Branches only pick date + hours.
  const dutyEnd = (branch?.duty_end || '').trim();
  const parseHHMM = (s) => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec((s || '').trim());
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  };
  const fmtHHMM = (mins) => {
    const clamped = Math.min(Math.max(mins || 0, 0), 24 * 60 - 1);
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const previewStart = dutyEnd || '';
  const previewEnd = (() => {
    const base = parseHHMM(previewStart);
    if (base === null) return '';
    return fmtHHMM(base + Math.max(Number(otHours) || 0, 0) * 60);
  })();
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [desk, setDesk] = useState(null);
  const [deskLoading, setDeskLoading] = useState(true);
  // Aug 2026 — requester self-edit dialog (pending rows only).
  const [editing, setEditing] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branch/overtime-requests`, { headers: authHeaders });
      setRequests(data.requests || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load overtime history';
      toast.error(msg);
    }
  }, [authHeaders]);

  const loadDesk = useCallback(async () => {
    setDeskLoading(true);
    try {
      const { data } = await axios.get(`${API}/branch/desk/mine`, { headers: authHeaders });
      setDesk(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load desk activity';
      toast.error(msg);
    } finally { setDeskLoading(false); }
  }, [authHeaders]);

  useEffect(() => { loadRequests(); loadDesk(); }, [loadRequests, loadDesk]);

  const submitRequest = async () => {
    const hours = Math.max(0, Math.floor(Number(otHours) || 0));
    const total = hours * 60;
    if (total < 1) {
      toast.error('Enter at least 1 hour.');
      return;
    }
    if (total > 16 * 60) {
      toast.error('Duration cannot exceed 16 hours per request.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/branch/overtime-requests`, {
        date: otDate,
        duration_minutes: total,
        reason: otReason.trim(),
      }, { headers: authHeaders });
      toast.success('Overtime request submitted for review.');
      setOtHours(2); setOtReason('');
      loadRequests();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to submit request';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const openEdit = (r) => {
    const mins = Number(r.requested_minutes ?? r.duration_minutes ?? 0);
    setEditing({
      id: r.id,
      date: r.date,
      hours: Math.max(1, Math.round(mins / 60)),
      reason: r.reason || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const hours = Math.max(0, Math.floor(Number(editing.hours) || 0));
    const total = hours * 60;
    if (total < 1) { toast.error('Enter at least 1 hour.'); return; }
    if (total > 16 * 60) { toast.error('Duration cannot exceed 16 hours per request.'); return; }
    setEditSaving(true);
    try {
      await axios.patch(`${API}/branch/overtime-requests/${editing.id}`, {
        date: editing.date,
        duration_minutes: total,
        reason: (editing.reason || '').trim(),
      }, { headers: authHeaders });
      toast.success('Request updated.');
      setEditing(null);
      loadRequests();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update request';
      toast.error(msg);
    } finally { setEditSaving(false); }
  };

  const cardCls = 'rounded-2xl border border-neutral-200 bg-white overflow-hidden';

  return (
    <BranchShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="branch-my-work-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0F172A]">My work</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Submit overtime claims and track when you step away from your desk.
            </p>
          </div>
          <DeskButton basePath="/branch" authHeaders={authHeaders} onChanged={loadDesk} />
        </div>

        {/* Overtime request card */}
        <div className={cardCls}>
          <div className="px-5 pt-5 pb-3 border-b border-neutral-100">
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Overtime</div>
            <div className="text-lg font-extrabold text-[#0F172A]">Submit a new request</div>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Date
              </span>
              <input
                type="date"
                value={otDate}
                min={mudDate()}
                onChange={(e) => setOtDate(e.target.value)}
                className="mt-1 w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                data-testid="branch-ot-date-input"
              />
            </label>
            <div className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Overtime hours
              </span>
              <input
                type="number" min="1" max="16" step="1" value={otHours}
                onChange={(e) => setOtHours(Math.max(1, Math.min(16, parseInt(e.target.value || '0', 10))))}
                placeholder="e.g. 2"
                className="mt-1 h-10 w-full max-w-xs rounded-lg border border-neutral-300 px-3 text-sm text-center focus:ring-2 focus:ring-sky-500 focus:outline-none"
                data-testid="branch-ot-hours-input"
                aria-label="Overtime hours"
              />
              <div className="mt-1 text-[10px] text-neutral-500">
                {dutyEnd ? (
                  <span data-testid="branch-ot-window-preview">
                    Auto: <b className="font-mono">{previewStart}</b> → <b className="font-mono">{previewEnd}</b> ({otHours}h)
                  </span>
                ) : (
                  'Your duty end is not on file — overtime cannot be auto-computed. Ask HR to set your schedule.'
                )}
              </div>
            </div>
            <label className="block md:col-span-3">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Reason</span>
              <textarea
                value={otReason}
                onChange={(e) => setOtReason(e.target.value)}
                placeholder="Briefly describe what you worked on…"
                className="mt-1 w-full min-h-[72px] rounded-lg border border-neutral-300 p-3 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                data-testid="branch-ot-reason-input"
              />
            </label>
            <div className="md:col-span-3 flex justify-end">
              <button
                type="button"
                onClick={submitRequest}
                disabled={submitting}
                className="h-10 px-5 rounded-lg bg-[#0F172A] hover:bg-[#020617] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60"
                data-testid="branch-ot-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit request
              </button>
            </div>
          </div>
        </div>

        {/* Overtime history */}
        <div className={cardCls}>
          <div className="px-5 py-3 border-b border-neutral-100 text-sm font-bold text-[#0F172A]">
            My overtime requests
          </div>
          {requests.length === 0 ? (
            <div className="px-5 py-6 text-sm text-neutral-500 italic">No requests submitted yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Start</th>
                    <th className="text-left px-4 py-2">End</th>
                    <th className="text-left px-4 py-2">Total</th>
                    <th className="text-left px-4 py-2">Approved</th>
                    <th className="text-left px-4 py-2">Reason</th>
                    <th className="text-center px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Reviewer note</th>
                    <th className="text-left px-4 py-2">Reviewed</th>
                    <th className="text-right px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {requests.map((r) => (
                    <tr key={r.id} data-testid={`branch-ot-row-${r.id}`}>
                      <td className="px-4 py-2 font-mono">{r.date}</td>
                      <td className="px-4 py-2 font-mono text-neutral-700" data-testid={`branch-ot-start-${r.id}`}>{r.start_time || '—'}</td>
                      <td className="px-4 py-2 font-mono text-neutral-700" data-testid={`branch-ot-end-${r.id}`}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>{r.end_time || '—'}</span>
                          <OvertimeSlaChip row={r} dataTestPrefix="branch-ot-sla" />
                        </div>
                      </td>
                      <td className="px-4 py-2 font-semibold" data-testid={`branch-ot-total-${r.id}`}>{r.requested_display || r.duration_display}</td>
                      <td className="px-4 py-2 font-semibold text-emerald-700">{r.approved_display || '—'}</td>
                      <td className="px-4 py-2 text-neutral-600 max-w-[320px] truncate" title={r.reason}>{r.reason || '—'}</td>
                      <td className="px-4 py-2 text-center"><StatusChip status={r.status} /></td>
                      <td className="px-4 py-2 text-neutral-600 max-w-[220px] truncate" title={r.review_note}>{r.review_note || '—'}</td>
                      <td className="px-4 py-2 text-neutral-500">{r.reviewed_at_display || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {r.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-neutral-300 hover:bg-neutral-50 text-[11px] font-semibold text-neutral-700"
                            data-testid={`branch-ot-edit-${r.id}`}
                            title="Edit this pending request"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Requester edit dialog */}
        {editing && (
          <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="branch-ot-edit-dialog">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
                <div className="text-sm font-bold text-[#0F172A]">Edit overtime request</div>
                <button type="button" onClick={() => setEditing(null)} className="text-neutral-400 hover:text-neutral-600" data-testid="branch-ot-edit-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Date</span>
                  <input
                    type="date"
                    value={editing.date}
                    min={mudDate()}
                    onChange={(e) => setEditing((v) => ({ ...v, date: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm"
                    data-testid="branch-ot-edit-date"
                  />
                </label>
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Overtime hours</span>
                  <input
                    type="number" min="1" max="16" step="1" value={editing.hours}
                    onChange={(e) => setEditing((v) => ({ ...v, hours: Math.max(1, Math.min(16, parseInt(e.target.value || '0', 10))) }))}
                    className="mt-1 h-10 w-full rounded-lg border border-neutral-300 px-3 text-sm text-center"
                    data-testid="branch-ot-edit-hours"
                  />
                  <div className="mt-1 text-[10px] text-neutral-500">
                    Backend will re-derive the window from your <span className="font-mono">duty_end</span> and this duration.
                  </div>
                </div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Reason</span>
                  <textarea
                    value={editing.reason}
                    onChange={(e) => setEditing((v) => ({ ...v, reason: e.target.value }))}
                    className="mt-1 w-full min-h-[72px] rounded-lg border border-neutral-300 p-3 text-sm"
                    data-testid="branch-ot-edit-reason"
                  />
                </label>
              </div>
              <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(null)} className="h-9 px-4 rounded-lg border border-neutral-300 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={editSaving}
                  className="h-9 px-4 rounded-lg bg-[#0F172A] hover:bg-[#020617] text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60"
                  data-testid="branch-ot-edit-save"
                >
                  {editSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desk activity */}
        <div className={cardCls}>
          <div className="px-5 py-3 border-b border-neutral-100 text-sm font-bold text-[#0F172A]">
            Desk activity — <span className="font-normal text-neutral-500">{desk?.month || ''}</span>
          </div>
          {deskLoading ? (
            <div className="px-5 py-6 text-sm text-neutral-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : (desk?.rows || []).length === 0 ? (
            <div className="px-5 py-6 text-sm text-neutral-500 italic">No desk activity recorded this month.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Out</th>
                    <th className="text-left px-4 py-2">In</th>
                    <th className="text-left px-4 py-2">Duration</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-left px-4 py-2">Leave reason</th>
                    <th className="text-left px-4 py-2">Outcome</th>
                    <th className="text-left px-4 py-2">Failure reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {(desk?.rows || []).map((r) => (
                    <tr key={r.id} data-testid={`branch-desk-row-${r.id}`}>
                      <td className="px-4 py-2 tabular-nums">{r.out_at_display || '—'}</td>
                      <td className="px-4 py-2 tabular-nums">{r.in_at_display || <span className="text-amber-700 font-semibold">Open</span>}</td>
                      <td className="px-4 py-2 font-semibold">{r.duration_display || '—'}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-neutral-100 text-neutral-700">
                          {r.category_label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-neutral-600 max-w-[240px] truncate" title={r.reason}>{r.reason || '—'}</td>
                      <td className="px-4 py-2">
                        {r.outcome === 'success' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800" data-testid={`branch-desk-outcome-${r.id}`}>Success</span>
                        )}
                        {r.outcome === 'unsuccess' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800" data-testid={`branch-desk-outcome-${r.id}`}>Unsuccess</span>
                        )}
                        {!r.outcome && <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-neutral-600 max-w-[280px] truncate" title={r.failure_reason}>{r.failure_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </BranchShell>
  );
};

export default BranchMyWork;
